import { create } from "zustand";

import { track } from "@/lib/analytics";

export type UpgradePlanGroup = "starter" | "pro" | "agency";

export type UpgradeDialogContext = {
  /** Human-friendly reason shown as a banner (usually the server limit message). */
  reason?: string;
  /** The entitlement feature that tripped the limit, for analytics/context. */
  feature?: string;
  /** Tier to emphasize (from a limit's `upgradePlanCode`). */
  highlightPlan?: UpgradePlanGroup;
  /**
   * Which control opened the dialog, for analytics, when there is no limit
   * `feature` to say why — a plain "Upgrade" button.
   */
  source?: string;
};

type UpgradeDialogStore = {
  open: boolean;
  context: UpgradeDialogContext | null;
  openUpgradeDialog: (context?: UpgradeDialogContext) => void;
  closeUpgradeDialog: () => void;
};

/**
 * Global upgrade/pricing dialog state. A Zustand store (not React context) so a
 * limit can open it imperatively from outside React — e.g. the tRPC
 * `MutationCache.onError` handler that fires whenever a plan limit is hit.
 */
export const useUpgradeDialogStore = create<UpgradeDialogStore>((set) => ({
  open: false,
  context: null,
  openUpgradeDialog: (context) => {
    // The one door every upgrade prompt goes through, so this counts them all.
    track("upgrade_prompt_viewed", {
      feature: context?.feature ?? null,
      highlight_plan: context?.highlightPlan ?? null,
      source: context?.source ?? null,
    });
    set({ context: context ?? null, open: true });
  },
  closeUpgradeDialog: () => set({ context: null, open: false }),
}));
